import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, Loader2, RefreshCw, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { SUBSCRIBER_EMAIL_STATUSES } from '../../lib/subscriberEmailStatus';

type Props = {
  subscriberId: string;
};

type ReferralCodeRow = {
  code: string;
};

type ReferralEventRow = {
  id: string;
  code: string;
  referred_subscriber_id: string;
  created_at: string;
  confirmed_at: string | null;
};

type ReferredSubscriberRow = {
  id: string;
  email: string | null;
  name: string | null;
  email_status: string | null;
  created_at: string | null;
  referred_by_code: string | null;
};

export type ReferredPerson = {
  id: string;
  email: string;
  name: string;
  email_status: string;
  email_status_label: string;
  signup_date: string | null;
  attribution: 'confirmed' | 'pending';
};

const PUBLIC_SUBSCRIBE_ORIGIN = 'https://stephansmith.me';

function emailStatusLabel(status: string | null | undefined): string {
  const value = (status ?? '').trim() || 'unknown';
  return SUBSCRIBER_EMAIL_STATUSES.find((s) => s.value === value)?.label ?? value;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

async function copyText(label: string, text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`);
  }
}

export default function SubscriberReferralsSection({ subscriberId }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [referred, setReferred] = useState<ReferredPerson[]>([]);

  const shareUrl = useMemo(
    () => (code ? `${PUBLIC_SUBSCRIBE_ORIGIN}/subscribe?ref=${encodeURIComponent(code)}` : ''),
    [code],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: codeRow, error: codeErr } = await supabase
        .from('referral_codes')
        .select('code')
        .eq('subscriber_id', subscriberId)
        .maybeSingle<ReferralCodeRow>();

      if (codeErr) throw codeErr;

      const referralCode = codeRow?.code?.trim() ?? null;
      setCode(referralCode);

      if (!referralCode) {
        setConfirmedCount(0);
        setPendingCount(0);
        setReferred([]);
        return;
      }

      const { data: events, error: eventsErr } = await supabase
        .from('referral_events')
        .select('id, code, referred_subscriber_id, created_at, confirmed_at')
        .eq('referrer_subscriber_id', subscriberId)
        .order('created_at', { ascending: false });

      if (eventsErr) throw eventsErr;

      const eventRows = (events ?? []) as ReferralEventRow[];
      const confirmedIds = new Set(eventRows.map((e) => e.referred_subscriber_id));

      const { data: pendingRows, error: pendingErr } = await supabase
        .from('subscribers')
        .select('id, email, name, email_status, created_at, referred_by_code')
        .eq('referred_by_code', referralCode)
        .order('created_at', { ascending: false });

      if (pendingErr) throw pendingErr;

      const pendingOnly = ((pendingRows ?? []) as ReferredSubscriberRow[]).filter(
        (row) => !confirmedIds.has(row.id),
      );

      const confirmedSubscriberIds = [...confirmedIds];
      let confirmedSubs: ReferredSubscriberRow[] = [];
      if (confirmedSubscriberIds.length) {
        const { data: confirmedData, error: confirmedErr } = await supabase
          .from('subscribers')
          .select('id, email, name, email_status, created_at, referred_by_code')
          .in('id', confirmedSubscriberIds);
        if (confirmedErr) throw confirmedErr;
        confirmedSubs = (confirmedData ?? []) as ReferredSubscriberRow[];
      }

      const subById = new Map<string, ReferredSubscriberRow>();
      for (const row of [...confirmedSubs, ...pendingOnly]) {
        subById.set(row.id, row);
      }

      const referredList: ReferredPerson[] = [];

      for (const event of eventRows) {
        const sub = subById.get(event.referred_subscriber_id);
        const status = sub?.email_status ?? 'confirmed';
        referredList.push({
          id: event.referred_subscriber_id,
          email: sub?.email?.trim() || '—',
          name: sub?.name?.trim() || '—',
          email_status: status,
          email_status_label: emailStatusLabel(status),
          signup_date: sub?.created_at ?? event.created_at,
          attribution: 'confirmed',
        });
      }

      for (const sub of pendingOnly) {
        const status = sub.email_status ?? 'pending';
        referredList.push({
          id: sub.id,
          email: sub.email?.trim() || '—',
          name: sub.name?.trim() || '—',
          email_status: status,
          email_status_label: emailStatusLabel(status),
          signup_date: sub.created_at,
          attribution: 'pending',
        });
      }

      referredList.sort((a, b) => {
        const aTime = a.signup_date ? new Date(a.signup_date).getTime() : 0;
        const bTime = b.signup_date ? new Date(b.signup_date).getTime() : 0;
        return bTime - aTime;
      });

      setConfirmedCount(eventRows.length);
      setPendingCount(pendingOnly.length);
      setReferred(referredList);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load referrals';
      setError(msg);
      setCode(null);
      setConfirmedCount(0);
      setPendingCount(0);
      setReferred([]);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Users className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" aria-hidden />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Referrals
          </h2>
          {!loading && !error ? (
            <>
              <span
                className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium tabular-nums text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200"
                title="Confirmed referrals"
              >
                {confirmedCount} confirmed
              </span>
              <span
                className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium tabular-nums text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
                title="Pending confirmation"
              >
                {pendingCount} pending
              </span>
              {code ? (
                <span className="inline-flex items-center gap-1.5 ml-1">
                  <code className="rounded bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                    {code}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyText('Link', shareUrl)}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    title="Copy subscribe link with referral code"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </span>
              ) : null}
            </>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50"
          title="Refresh referrals"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      {error ? (
        <div className="px-6 py-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      ) : loading && !code ? (
        <div className="px-6 py-8 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading referrals…
        </div>
      ) : (
        <div className="px-6 py-5">
          {referred.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No referrals yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Email</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Name</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Date</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Email status</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Attribution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {referred.map((row) => (
                    <tr key={`${row.id}-${row.attribution}`}>
                      <td className="px-4 py-2">
                        <Link
                          to={`/subscribers/${row.id}`}
                          className="text-emerald-700 hover:underline dark:text-emerald-400"
                        >
                          {row.email}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-gray-800 dark:text-gray-200">{row.name}</td>
                      <td className="px-4 py-2 tabular-nums text-gray-600 dark:text-gray-300">
                        {formatWhen(row.signup_date)}
                      </td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-200">{row.email_status_label}</td>
                      <td className="px-4 py-2">
                        {row.attribution === 'confirmed' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                            <Check className="h-3.5 w-3.5" />
                            Confirmed
                          </span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-300">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
